/**
 * @file LocalizedRoutesContext.tsx
 * @description React Context exposing the set of internal paths that are actually
 * localized in the active locale. Consumed when deciding whether an internal link
 * should carry the locale prefix (e.g. /es) — English-only pages must stay unprefixed.
 * The set is resolved server-side from the routes index and handed down via Providers.
 */

'use client';

import { createContext, useContext } from 'react';

/**
 * Context value interface for localized routes.
 */
interface LocalizedRoutesContextValue {
  /** Lowercased, prefix-free paths that exist as localized pages in the active locale */
  localizedUrls: Set<string>;
}

/**
 * React Context for the active locale's localized paths.
 * Defaults to an empty set so consumers rendered outside a provider (e.g. Storybook)
 * fall back to the prefix-always behavior rather than crashing.
 */
const LocalizedRoutesContext = createContext<LocalizedRoutesContextValue>({
  localizedUrls: new Set(),
});

/** Provider component for LocalizedRoutesContext. */
export const LocalizedRoutesProvider = LocalizedRoutesContext.Provider;

/** Custom hook to access the active locale's localized paths from context. */
export const useLocalizedRoutes = () => useContext(LocalizedRoutesContext);
